import { Box, Typography } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import type { FC } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';
import UnassignDeviceIcon from 'components/Icons/UnassignDeviceIcon';
import Modal from 'components/Modal';
import StatusMessageBox from 'components/StatusMessageBox';

import { useAppDispatch } from 'redux-store';
import deleteDeviceMemberAccessThunk from 'redux-store/thunks/organizationOverview/deleteDeviceMemberAccessThunk';
import getOrganizationByIdThunk from 'redux-store/thunks/organizations/getOrganizationByIdThunk';

interface Props extends DefaultPopupProps {
  organizationId: number | null;
  deviceIds: number[];
  memberId: number | null;
}

const UnassignDevicesFromMemberModal: FC<Props> = ({
  onClose,
  organizationId,
  deviceIds,
  memberId,
  open,
}) => {
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleUnassignMemebersFromDevice = useCallback(async () => {
    if (!organizationId || !memberId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await dispatch(
        deleteDeviceMemberAccessThunk({
          organizationId: organizationId,
          data: {
            user_assignments: [
              {
                user_id: memberId,
                device_ids: deviceIds,
              },
            ],
          },
        }),
      ).unwrap();

      if (!result.success) {
        setError(
          t(
            'common:pages.organization_overview.unassign_devices_from_member.error',
          ),
        );

        return;
      }

      setSuccess(true);

      await new Promise(resolve => setTimeout(resolve, 1000));

      await dispatch(
        getOrganizationByIdThunk({
          organizationId,
        }),
      ).unwrap();

      onClose();
      setSuccess(false);
    } catch (e) {
      Sentry.captureException(e, {
        extra: { organizationId, memberId, deviceIds },
        tags: { page: 'UnAssignDevicesFromMember' },
      });

      setError(
        t(
          'common:pages.organization_overview.unassign_devices_from_member.error',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, memberId, deviceIds, onClose, organizationId, t]);

  return (
    <Modal open={open} onClose={onClose} hideCloseButton={success}>
      {!success ? (
        <Box height="100%" sx={{ overflowY: 'auto' }}>
          <Box mb={4} data-testid="unassign-devices-from-member-modal">
            <Typography
              variant="h2"
              textAlign="center"
              data-testid="unassign-devices-from-member-modal-title"
            >
              {t(
                'common:pages.organization_overview.unassign_devices_from_member.title',
              )}
            </Typography>
          </Box>
          <Box mb={4}>
            <Typography variant="body1" textAlign="center">
              {t(
                'common:pages.organization_overview.unassign_devices_from_member.description',
              )}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" gap={1} width="100%">
            <Button
              onClick={onClose}
              color="neutral"
              fullWidth
              variant="flat"
              data-testid="cancel-button"
            >
              {t('common:cancel')}
            </Button>
            <Button
              disabled={isLoading}
              onClick={handleUnassignMemebersFromDevice}
              startIcon={<UnassignDeviceIcon />}
              fullWidth
              variant="flat"
              color="error"
              data-testid="submit"
            >
              {t('common:unassign')}
            </Button>
          </Box>
          <ErrorText error={error} mt={2} />
        </Box>
      ) : (
        <StatusMessageBox
          type="success"
          message={t(
            'common:pages.organization_overview.unassign_devices_from_member.success',
          )}
        />
      )}
    </Modal>
  );
};

export default UnassignDevicesFromMemberModal;
